/**
 * ConstructionTableComponent Component
 * 
 * Main table component for displaying construction worker payslip details.
 * Renders a complex table structure with multiple sections:
 * - Header (組織コード, 社員コード, 氏名, title)
 * - 振込 (Transfer) section
 * - 勤怠 (Attendance) section
 * - 支給 (Payment) section
 * - 控除 (Deduction) section
 * - その他 (Other) section
 * - 労務費明細書 (Labor Cost Detail) section - separate table
 * - Company name and memo sections
 * 
 * @component
 */

import React from 'react';
import '../../css/ConstructionTableComponent.css';

/** Smaller font tiers for long names in the narrow 氏名 column (~50px). */
function employeeNameDisplayFontSize(displayWithSama: string): string | undefined {
  const len = displayWithSama.length;
  if (len > 14) return '8px';
  if (len > 10) return '9px';
  if (len > 7) return '10px';
  return undefined;
}

/**
 * Props interface for ConstructionTableComponent component.
 */
interface ConstructionTableComponentProps {
  dataType: 'construction';  // Type of payslip data ('construction')
  selectedYear: string;      // Selected year for payslip
  selectedMonth: string;     // Selected month for payslip
  empName: string;           // Employee name
  selectedSite: string;      // Selected construction site
  hasSearched: boolean;      // Whether a search has been performed
  constructionData: any;     // Fetched payslip data object
  payslipSettings: {         // Payslip appearance settings
    borderColor: string;
    backgroundColor: string;
    roumuhiBorderColor: string;
    roumuhiBackgroundColor: string;
    fontSize: string;
    fontFamily: string;
    showCompanyName: boolean;
    showEmployeeCode: boolean;
    showMemo: boolean;
    furikomi: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    payment: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    deduction: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    attendance: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    notes: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
    sonota?: {
      enabled: string[];
      lastUpdated: string;
      updatedBy: string;
    };
  };
  furikomiItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  paymentItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  deductionItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  attendanceItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  notesItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
  sonotaItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;
}

/**
 * ConstructionTableComponent Component
 * 
 * Renders the payslip table with all sections and data.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const ConstructionTableComponent: React.FC<ConstructionTableComponentProps> = ({
  dataType,
  selectedYear,
  selectedMonth,
  empName,
  selectedSite,
  hasSearched,
  constructionData,
  payslipSettings,
  furikomiItems,
  paymentItems,
  deductionItems,
  attendanceItems,
  notesItems,
  sonotaItems
}) => {
  /**
   * Debug effect: Logs daily attendance fields when construction data changes.
   * 
   * Checks for daily attendance data (day1 through day31) in the construction data.
   * Used for debugging data structure issues.
   */
  React.useEffect(() => {
    if (constructionData) {
      // Check daily attendance fields
      const dailyFields = [];
      for (let i = 1; i <= 31; i++) {
        const fieldName = `day${i}`;
        if (constructionData[fieldName] !== undefined) {
          dailyFields.push(`${fieldName}: ${constructionData[fieldName]}`);
        }
      }
      // Check allowance fields
    }
  }, [constructionData]);

  /**
   * Apply CSS custom properties for dynamic styling.
   * 
   * Updates CSS variables on document root when settings change,
   * allowing all table cells to use the same colors and fonts.
   */
  React.useEffect(() => {
    // Apply color settings for main table
    document.documentElement.style.setProperty('--payslip-border-color', payslipSettings.borderColor);
    document.documentElement.style.setProperty('--payslip-background-color', payslipSettings.backgroundColor);
    
    // Apply color settings for roumuhi (labor cost) section
    document.documentElement.style.setProperty('--payslip-roumuhi-border-color', payslipSettings.roumuhiBorderColor);
    document.documentElement.style.setProperty('--payslip-roumuhi-background-color', payslipSettings.roumuhiBackgroundColor);
    
    // Apply typography settings
    document.documentElement.style.setProperty('--payslip-font-size', payslipSettings.fontSize);
    document.documentElement.style.setProperty('--payslip-font-family', payslipSettings.fontFamily);
  }, [payslipSettings.borderColor, payslipSettings.backgroundColor, payslipSettings.roumuhiBorderColor, payslipSettings.roumuhiBackgroundColor, payslipSettings.fontSize, payslipSettings.fontFamily]);

  /**
   * Set initial CSS variables immediately on component mount.
   * 
   * Ensures CSS variables are available even before the main useEffect runs,
   * preventing FOUC (Flash of Unstyled Content).
   */
  React.useEffect(() => {
    // Set initial values with defaults to ensure they're available immediately
    document.documentElement.style.setProperty('--payslip-border-color', payslipSettings.borderColor || '#4caf50');
    document.documentElement.style.setProperty('--payslip-background-color', payslipSettings.backgroundColor || '#eaffea');
    document.documentElement.style.setProperty('--payslip-roumuhi-border-color', payslipSettings.roumuhiBorderColor || '#FF8C00');
    document.documentElement.style.setProperty('--payslip-roumuhi-background-color', payslipSettings.roumuhiBackgroundColor || '#FFD4A3');
    document.documentElement.style.setProperty('--payslip-font-size', payslipSettings.fontSize || '10px');
    document.documentElement.style.setProperty('--payslip-font-family', payslipSettings.fontFamily || 'Yu Gothic');
  }, []); // Run only once on mount

  // Payslip title
  const title = '給与明細書';

  /**
   * Filter enabled items for each section based on payslip settings.
   * 
   * Only displays fields that are enabled in the settings,
   * allowing users to customize which fields appear on the payslip.
   */
  const enabledFurikomiItems = furikomiItems.filter(item => 
    payslipSettings.furikomi.enabled.includes(item.id)
  );
  const enabledPaymentItems = paymentItems.filter(item => 
    payslipSettings.payment.enabled.includes(item.id)
  );
  const enabledDeductionItems = deductionItems.filter(item => 
    payslipSettings.deduction.enabled.includes(item.id)
  );
  const enabledSonotaItems = sonotaItems.filter(item =>
    (payslipSettings.sonota && payslipSettings.sonota.enabled) ? payslipSettings.sonota.enabled.includes(item.id) : true
  );
  const enabledAttendanceItems = attendanceItems.filter(item => 
    payslipSettings.attendance.enabled.includes(item.id)
  );
  const enabledNotesItems = notesItems.filter(item => 
    payslipSettings.notes.enabled.includes(item.id)
  );

  /**
   * Fills empty cells to maintain consistent table structure.
   * 
   * Adds empty placeholder items to ensure rows have consistent width.
   * Used to maintain table layout when fewer fields are enabled than the maximum.
   * 
   * @param enabledItems - Array of enabled items
   * @param maxCells - Maximum number of cells in the row
   * @returns Array of items padded with empty placeholders
   */
  const fillEmptyCells = (enabledItems: any[], maxCells: number) => {
    const filled = [...enabledItems];
    while (filled.length < maxCells) {
      filled.push({ id: `empty_${filled.length}`, name: '', keyId: '', valueId: '' });
    }
    return filled;
  };

  /**
   * Generates array of day labels for the selected month.
   * 
   * Creates an array of day labels (e.g., "1日", "2日", ...) for the selected month.
   * Used for the 労務費明細書 (labor cost detail) section daily attendance table.
   * 
   * @param year - Selected year string
   * @param month - Selected month string
   * @returns Array of day labels (e.g., ["1日", "2日", ...])
   */
  const generateMonthDays = (year: string, month: string) => {
    // If year or month is not selected, use current date as default
    const yearNum = year ? parseInt(year) : new Date().getFullYear();
    const monthNum = month ? parseInt(month) : new Date().getMonth() + 1;
    
    // Validate the parsed values
    if (isNaN(yearNum) || isNaN(monthNum)) {
      // If parsing fails, use current date as fallback
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
      
      const days = [];
      for (let day = 1; day <= daysInMonth; day++) {
        days.push(`${day}日`);
      }
      return days;
    }
    
    // Calculate number of days in the selected month
    // Note: Using (year, month, 0) returns the last day of the previous month,
    // so (year, month, 0) gives us the last day of the specified month
    const daysInMonth = new Date(yearNum, monthNum, 0).getDate();
    
    // Generate day labels for all days in the month
    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(`${day}日`);
    }
    return days;
  };

  /** 勤怠 fields: decimals without thousands separators (DB scale 2). */
  const KINTAI_DECIMAL_KEY_IDS = new Set([
    'ca_k1', 'ca_k2', 'ca_k3', 'ca_k4', 'ca_k5', 'ca_k6',
    'attendanceDays', 'paidLeaveDays', 'remainingPaidLeave',
    'overtimeHours', 'nightShiftHours', 'holidayHours',
  ]);

  const toNumericValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') return null;
      const num = Number(trimmed);
      return Number.isFinite(num) ? num : null;
    }
    return null;
  };

  /**
   * Formats numbers preserving up to 2 decimal places (matches DB DECIMAL scale).
   * @param useGrouping - true for currency; false for 勤怠 counts/hours
   */
  const formatNumber = (value: number, useGrouping: boolean = true): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      useGrouping,
    });
  };

  /**
   * Gets data value from constructionData by keyId.
   * 
   * Maps configuration keyIds to actual database field names.
   * Formats numbers using standard format and handles empty/null values.
   * 
   * @param keyId - Configuration key ID (e.g., 'ca_k1', 'cp_k1')
   * @param defaultValue - Default value to return if data not found (default: '')
   * @param hideZero - Whether to hide zero values (default: true, returns non-breaking space)
   * @returns Formatted value string or non-breaking space for empty values
   */
  const getDataValue = (keyId: string, defaultValue: any = '', hideZero: boolean = true) => {
    if (!constructionData) {
      return defaultValue;
    }
    
    /**
     * Map keyId to actual database field names.
     * 
     * Maps configuration IDs (from DataConfiguration.ts) to database field names:
     * - Attendance fields: ca_k1 -> attendanceDays, etc.
     * - Payment fields: cp_k1 -> baseSalary, etc.
     * - Deduction fields: cd_k1 -> healthInsurance, etc.
     * - Furikomi fields: cf_k1 -> transferAmount, etc.
     */
    const fieldMapping: { [key: string]: string } = {
      // Attendance fields (from DataConfiguration)
      'ca_k1': 'attendanceDays',        // 出勤日数
      'ca_k2': 'paidLeaveDays',         // 有給日数
      'ca_k3': 'remainingPaidLeave',    // 有給残日数
      'ca_k4': 'overtimeHours',         // 時間外
      'ca_k5': 'nightShiftHours',       // 深夜
      'ca_k6': 'holidayHours',          // 休日
      
      // Payment fields (from DataConfiguration)
      'cp_k1': 'baseSalary',            // 基本給
      'cp_k2': 'pieceRatePay',          // 出来高給
      'cp_k3': 'siteAllowance',         // 現場手当
      'cp_k4': 'overtimeAllowance',     // 時間外手当
      'cp_k5': 'nightShiftAllowance',   // 深夜手当
      'cp_k6': 'holidayAllowance',      // 休日手当
      'cp_k7': 'otherAllowance',        // その他手当
      
      // Deduction fields (from DataConfiguration)
      'cd_k1': 'healthInsurance',       // 健康保険
      'cd_k2': 'employmentInsurance',   // 雇用保険
      'cd_k3': 'pensionInsurance',      // 厚生年金
      'cd_k4': 'incomeTax',             // 所得税
      'cd_k5': 'residentTax',           // 住民税
      'cd_k6': 'mealCost',              // 食費
      'cd_k7': 'miscellaneousExpenses', // 諸式代
      'cd_k8': 'beddingCost',           // 布団代
      'cd_k9': 'leaseCost',             // 備品リース代
      'cd_k10': 'gasolineCost',         // ガソリン代
      'cd_k11': 'advancePayment',       // 仮払金
      'cd_k12': 'otherPayment',         // その他支給
      'cd_k13': 'otherDeduction',       // その他控除
      'cd_k14': 'yearEndRefund',        // 年末調整還付金
      
      // Furikomi fields (from DataConfiguration)
      'cf_k1': 'transferAmount',        // 振込金額
      'cf_v1': 'transferAmount',        // 振込金額 (value)
      
      // Legacy field mappings (for backward compatibility)
      'attendanceDays': 'attendanceDays',
      'totalWage': 'totalWage',
      'healthInsurance': 'healthInsurance',
      'employmentInsurance': 'employmentInsurance',
      'pensionInsurance': 'pensionInsurance',
      'incomeTax': 'incomeTax',
      'fixedReduction': 'fixedReduction',
      'residentTax': 'residentTax',
      'legalDeductionTotal': 'legalDeductionTotal',
      'deductedPayment1': 'deductedPayment1',
      'mealCost': 'mealCost',
      'miscellaneousExpenses': 'miscellaneousExpenses',
      'gasolineCost': 'gasolineCost',
      'advancePayment': 'advancePayment',
      'beddingCost': 'beddingCost',
      'leaseCost': 'leaseCost',
      'agreementDeductionTotal': 'agreementDeductionTotal',
      'totalDeductions': 'totalDeductions',
      'deductedPayment2': 'deductedPayment2',
      'otherPayment': 'otherPayment',
      'otherDeduction': 'otherDeduction',
      'yearEndRefund': 'yearEndRefund',
      'transferAmount': 'transferAmount',
      'paidLeaveDays': 'paidLeaveDays',
      'remainingPaidLeave': 'remainingPaidLeave',
      'overtimeHours': 'overtimeHours',
      'nightShiftHours': 'nightShiftHours',
      'holidayHours': 'holidayHours',
      'baseSalary': 'baseSalary',
      'pieceRatePay': 'pieceRatePay',
      'siteAllowance': 'siteAllowance',
      'overtimeAllowance': 'overtimeAllowance',
      'nightShiftAllowance': 'nightShiftAllowance',
      'holidayAllowance': 'holidayAllowance',
      'otherAllowance': 'otherAllowance',
      'incomeTax2': 'incomeTax2',
      // Notes fields (その他支給内訳, その他控除内訳, 備考)
      'otherPaymentBreakdown': 'otherPaymentBreakdown',
      'otherDeductionBreakdown': 'otherDeductionBreakdown',
      'remarks': 'remarks'
    };

    // Get field name from mapping or use keyId directly if not mapped
    const fieldName = fieldMapping[keyId] || keyId;
    const value = constructionData[fieldName];
    
    // Debug logging for first few calls (commented out in production)
    if (keyId === 'totalWage' || keyId === 'baseSalary' || keyId === 'attendanceDays' || 
        keyId === 'ca_k1' || keyId === 'ca_k2' || keyId === 'ca_k3' || keyId === 'ca_k4' ||
        keyId === 'mealCost' || keyId === 'miscellaneousExpenses' || keyId === 'beddingCost' || 
        keyId === 'leaseCost' || keyId === 'gasolineCost' || keyId === 'advancePayment' || 
        keyId === 'otherPayment' || keyId === 'otherDeduction' || keyId === 'totalDeductions') {
      // Debug logging code here if needed
    }
    
    const numericValue = toNumericValue(value);
    if (numericValue !== null) {
      if (hideZero && numericValue === 0) return '\u00A0';
      const useGrouping = !KINTAI_DECIMAL_KEY_IDS.has(keyId);
      return formatNumber(numericValue, useGrouping);
    }

    if (value === null || value === undefined) {
      return defaultValue === '' ? '\u00A0' : defaultValue;
    }
    const stringValue = value.toString();
    return stringValue === '' ? '\u00A0' : stringValue;
  };

  /**
   * Gets daily attendance value for roumuhi section (always shows zeros).
   * 
   * Retrieves daily attendance data for a specific day (day1 through day31).
   * Always shows zero values (unlike getDataValue) for roumuhi section.
   * 
   * @param dayNumber - Day number (1-31)
   * @returns Formatted daily value string or empty string if not found
   */
  const getDailyValue = (dayNumber: number) => {
    if (!constructionData) {
      return '';
    }
    
    const dayField = `day${dayNumber}`;
    const value = constructionData[dayField];
    
    // Debug logging for first few days (commented out in production)
    if (dayNumber <= 5) {
      // Debug logging code here if needed
    }
    
    if (value === null || value === undefined) return '';
    const numericValue = toNumericValue(value);
    if (numericValue !== null) {
      return formatNumber(numericValue, false);
    }
    return value.toString();
  };

  const filledPaymentItems = fillEmptyCells(enabledPaymentItems, 6);
  const filledDeductionItems = fillEmptyCells(enabledDeductionItems, 6);
  const filledAttendanceItems = fillEmptyCells(enabledAttendanceItems, 6);
  const filledNotesItems = fillEmptyCells(enabledNotesItems, 5);

  // Helper function to render key-value pairs for a section
  const renderKeyValueSection = (
    enabledItems: any[], 
    sectionName: string, 
    rowSpan: number,
    className: string
  ) => {
    if (enabledItems.length === 0) return null;

    return (
      <>
        <tr className={`${className} green-row`}>
          <td rowSpan={rowSpan} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">{sectionName}</td>
          {enabledItems.map((item, index) => (
            <td 
              key={item.keyId}
              id={item.keyId}
              className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${index === 5 ? 'construction-thick-border-right' : ''}`}
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: 6 - enabledItems.length }, (_, index) => (
            <td 
              key={`empty-${sectionName}-key-${index}`} 
              className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${(enabledItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
            ></td>
          ))}
        </tr>
        <tr className={className}>
          {enabledItems.map((item, index) => (
            <td 
              key={item.valueId}
              id={item.valueId}
              className={`construction-border-left construction-thick-border-bottom construction-no-top-border ${index === 5 ? 'construction-thick-border-right' : ''}`}
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: 6 - enabledItems.length }, (_, index) => (
            <td 
              key={`empty-${sectionName}-value-${index}`} 
              className={`construction-border-left construction-thick-border-bottom construction-no-top-border ${(enabledItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
            ></td>
          ))}
        </tr>
      </>
    );
  };

  // Helper function to render multi-row sections (4 rows, 6 rows)
  const renderMultiRowSection = (
    enabledItems: any[], 
    sectionName: string, 
    rowSpan: number,
    className: string
  ) => {
    if (enabledItems.length === 0) return null;

    // Special handling for 勤怠 section
    if (sectionName === '勤怠') {
      const kintaiRowOrder = ['出勤日数', '有給日数', '有給残日数', '時間外', '深夜', '休日'] as const;
      const row1Items = kintaiRowOrder
        .map((name) => enabledItems.find((item) => item.name === name))
        .filter((item): item is NonNullable<typeof item> => item != null);
      const remainingItems = enabledItems.filter(
        (item) => !kintaiRowOrder.includes(item.name as (typeof kintaiRowOrder)[number])
      );

      return (
        <>
          {/* Row 1 - 出勤日数, 有給日数, 有給残日数, 時間外, 深夜, 休日 */}
          <tr className={`${className} green-row`}>
            <td rowSpan={rowSpan} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">{sectionName}</td>
            {row1Items.map((item, index) => (
              <td 
                key={item.keyId}
                id={item.keyId}
                className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${index === 5 ? 'construction-thick-border-right' : ''}`}
              >
                {item.name}
              </td>
            ))}
            {Array.from({ length: 6 - row1Items.length }, (_, index) => (
              <td 
                key={`empty-${sectionName}-row1-${index}`} 
                className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${(row1Items.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
              >{'\u00A0'}</td>
            ))}
          </tr>
          
          {/* Row 2 - Values for row 1 items */}
          <tr className={className}>
            {row1Items.map((item, index) => (
              <td 
                key={item.valueId}
                id={item.valueId}
                className={`construction-border-left construction-border-top ${
                  remainingItems.length > 0
                    ? 'construction-border-bottom'
                    : 'construction-thick-border-bottom'
                } ${index === 5 ? 'construction-thick-border-right' : ''}`}
              >
                {getDataValue(item.keyId, '', false)}
              </td>
            ))}
            {Array.from({ length: 6 - row1Items.length }, (_, index) => (
              <td 
                key={`empty-${sectionName}-row2-${index}`} 
                className={`construction-border-left construction-border-top ${
                  remainingItems.length > 0
                    ? 'construction-border-bottom'
                    : 'construction-thick-border-bottom'
                } ${(row1Items.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
              >{'\u00A0'}</td>
            ))}
          </tr>
          {remainingItems.length > 0 && (
            <>
              <tr className={`${className} green-row`}>
                {remainingItems.map((item, index) => (
                  <td
                    key={`${sectionName}-extra-key-${item.keyId}`}
                    className={`construction-border-left construction-border-top construction-border-bottom ${index === 5 ? 'construction-thick-border-right' : ''}`}
                  >
                    {item.name}
                  </td>
                ))}
                {Array.from({ length: 6 - remainingItems.length }, (_, index) => (
                  <td
                    key={`empty-${sectionName}-extra-key-${index}`}
                    className={`construction-border-left construction-border-top construction-border-bottom ${(remainingItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
                  >{'\u00A0'}</td>
                ))}
              </tr>
              <tr className={className}>
                {remainingItems.map((item, index) => (
                  <td
                    key={`${sectionName}-extra-val-${item.valueId}`}
                    className={`construction-border-left construction-thick-border-bottom construction-border-top ${index === 5 ? 'construction-thick-border-right' : ''}`}
                  >
                    {getDataValue(item.keyId, '', false)}
                  </td>
                ))}
                {Array.from({ length: 6 - remainingItems.length }, (_, index) => (
                  <td
                    key={`empty-${sectionName}-extra-val-${index}`}
                    className={`construction-border-left construction-thick-border-bottom construction-border-top ${(remainingItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
                  >{'\u00A0'}</td>
                ))}
              </tr>
            </>
          )}
        </>
      );
    }

    // Default handling for other sections
    const itemsPerRow = Math.ceil(enabledItems.length / (rowSpan - 1));
    const rows = [];
    
    for (let i = 0; i < rowSpan - 1; i++) {
      const startIndex = i * itemsPerRow;
      const endIndex = Math.min(startIndex + itemsPerRow, enabledItems.length);
      const rowItems = enabledItems.slice(startIndex, endIndex);
      
      rows.push(
        <tr key={`${sectionName}-row-${i}`} className={`${className} ${i % 2 === 0 ? 'green-row' : ''}`}>
          {i === 0 && (
            <td rowSpan={rowSpan} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">{sectionName}</td>
          )}
          {rowItems.map((item, index) => (
            <td 
              key={item.keyId}
              id={item.keyId}
              className={`${i === 0 ? 'construction-thick-border-top' : 'construction-border-top'} construction-border-left construction-border-bottom ${index === 5 ? 'construction-thick-border-right' : ''}`}
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: 6 - rowItems.length }, (_, index) => (
            <td 
              key={`empty-${sectionName}-row-${i}-${index}`} 
              className={`${i === 0 ? 'construction-thick-border-top' : 'construction-border-top'} construction-border-left construction-border-bottom ${(rowItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
            ></td>
          ))}
        </tr>
      );
    }

    // Add the final value row
    const finalRowItems = enabledItems.slice(0, itemsPerRow);
    rows.push(
      <tr key={`${sectionName}-final-row`} className={className}>
        {finalRowItems.map((item, index) => (
          <td 
            key={item.valueId}
            id={item.valueId}
            className={`construction-border-left construction-thick-border-bottom construction-no-top-border ${index === 5 ? 'construction-thick-border-right' : ''}`}
          >
            {/* Placeholder value - can be replaced with actual data */}
            {index + 1}
          </td>
        ))}
        {Array.from({ length: 6 - finalRowItems.length }, (_, index) => (
          <td 
            key={`empty-${sectionName}-final-${index}`} 
            className={`construction-border-left construction-thick-border-bottom construction-no-top-border ${(finalRowItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
          ></td>
        ))}
      </tr>
    );

    return rows;
  };

  // Helper function to render the 支給 section with specific layout
  const renderShikyuSection = () => {
    // First row: up to 6 payment items; second row: remaining payment items + 支給合計
    const firstRowItems = enabledPaymentItems.slice(0, 6);
    const secondRowItems = enabledPaymentItems.slice(6, 11);
    const secondRowFillCount = Math.max(0, 5 - secondRowItems.length);
    
    return (
      <>
        {/* Row 1 - Items from settings (up to 6 items) */}
        <tr className="construction-shikyu-section green-row">
          <td rowSpan={4} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">支給</td>
          {firstRowItems.map((item, index) => (
            <td 
              key={`shikyu-key-${index}`}
              className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${index === 5 ? 'construction-thick-border-right' : ''}`}
            >
              {item.name}
            </td>
          ))}
          {/* Fill empty cells if less than 6 items */}
          {Array.from({ length: 6 - firstRowItems.length }, (_, index) => (
            <td 
              key={`shikyu-empty-key-${index}`}
              className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${(firstRowItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
            >{'\u00A0'}</td>
          ))}
        </tr>
        
        {/* Row 2 - Values for first row items (基本給 etc.) — same border/height pattern as 振込 */}
        <tr className="construction-shikyu-section">
          {firstRowItems.map((item, index) => (
            <td 
              key={`shikyu-value-${index}`}
              className={`construction-border-left construction-thick-border-bottom construction-border-top ${index === 5 ? 'construction-thick-border-right' : ''}`}
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {/* Fill empty cells if less than 6 items */}
          {Array.from({ length: 6 - firstRowItems.length }, (_, index) => (
            <td 
              key={`shikyu-empty-value-${index}`}
              className={`construction-border-left construction-thick-border-bottom construction-border-top ${(firstRowItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
            >{'\u00A0'}</td>
          ))}
        </tr>
        
        {/* Row 3 - Extra payment labels (e.g. その他手当) + 支給合計 in last cell */}
        <tr className="construction-shikyu-section green-row">
          {secondRowItems.map((item, index) => (
            <td
              key={`shikyu-second-key-${item.keyId}`}
              className="construction-border-left construction-border-top construction-border-bottom"
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: secondRowFillCount }, (_, index) => (
            <td 
              key={`shikyu-empty-${index}`}
              className="construction-border-left construction-border-top construction-border-bottom"
            >{'\u00A0'}</td>
          ))}
          {/* 支給合計 in last cell */}
          <td className="construction-border-left construction-border-top construction-border-bottom construction-thick-border-right">
            支給合計
          </td>
        </tr>
        
        {/* Row 4 - Extra payment values + value for 支給合計 in last cell */}
        <tr className="construction-shikyu-section">
          {secondRowItems.map((item) => (
            <td
              key={`shikyu-second-value-${item.valueId}`}
              className="construction-border-left construction-thick-border-bottom construction-no-top-border"
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: secondRowFillCount }, (_, index) => (
            <td 
              key={`shikyu-empty-value-${index}`}
              className="construction-border-left construction-thick-border-bottom construction-no-top-border"
            >{'\u00A0'}</td>
          ))}
          {/* Value for 支給合計 in last cell (賃金総額 from TXT — not recalculated) */}
          <td className="construction-border-left construction-thick-border-bottom construction-no-top-border construction-thick-border-right">
            {getDataValue('totalWage', '')}
          </td>
        </tr>
      </>
    );
  };

  // Helper function to render the 控除 section; uses enabled deduction items from settings.
  const renderKoujoSection = () => {
    const firstRowItems = deductionItems.slice(0, 5).filter(item => payslipSettings.deduction.enabled.includes(item.id));
    const thirdRowItems = deductionItems.slice(5, 10).filter(item => payslipSettings.deduction.enabled.includes(item.id));
    const fifthRowItems = deductionItems.slice(10, 11).filter(item => payslipSettings.deduction.enabled.includes(item.id));

    return (
      <>
        {/* Row 1 - 健康保険, 雇用保険, 厚生年金, 所得税, 住民税 (enabled only) */}
        <tr className="construction-koujo-section green-row">
          <td rowSpan={6} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">控除</td>
          {firstRowItems.map((item, index) => (
            <td 
              key={`koujo-key-1-${item.keyId}`}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: 5 - firstRowItems.length }, (_, index) => (
            <td key={`koujo-empty-1-${index}`} className="construction-thick-border-top construction-border-left construction-no-bottom-border">{'\u00A0'}</td>
          ))}
          <td className="construction-thick-border-top construction-border-left construction-no-bottom-border construction-thick-border-right">{'\u00A0'}</td>
        </tr>
        {/* Row 2 - Values for first row items */}
        <tr className="construction-koujo-section">
          {firstRowItems.map((item, index) => (
            <td 
              key={`koujo-value-1-${item.valueId}`}
              className="construction-border-left construction-border-top construction-border-bottom"
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: 5 - firstRowItems.length }, (_, index) => (
            <td key={`koujo-empty-v1-${index}`} className="construction-border-left construction-border-top construction-border-bottom">{'\u00A0'}</td>
          ))}
          <td className="construction-border-left construction-border-top construction-border-bottom construction-thick-border-right">{'\u00A0'}</td>
        </tr>
        {/* Row 3 - 食費, 諸式代, 布団代, 備品リース代, ガソリン代 (enabled only) */}
        <tr className="construction-koujo-section green-row">
          {thirdRowItems.map((item, index) => (
            <td 
              key={`koujo-key-3-${item.keyId}`}
              className="construction-border-left construction-border-top construction-border-bottom"
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: 5 - thirdRowItems.length }, (_, index) => (
            <td key={`koujo-empty-3-${index}`} className="construction-border-left construction-border-top construction-border-bottom">{'\u00A0'}</td>
          ))}
          <td className="construction-border-left construction-border-top construction-border-bottom construction-thick-border-right">{'\u00A0'}</td>
        </tr>
        {/* Row 4 - Values for third row items */}
        <tr className="construction-koujo-section">
          {thirdRowItems.map((item, index) => (
            <td 
              key={`koujo-value-3-${item.valueId}`}
              className="construction-border-left construction-border-top construction-border-bottom"
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: 5 - thirdRowItems.length }, (_, index) => (
            <td key={`koujo-empty-v3-${index}`} className="construction-border-left construction-border-top construction-border-bottom">{'\u00A0'}</td>
          ))}
          <td className="construction-border-left construction-border-top construction-border-bottom construction-thick-border-right">{'\u00A0'}</td>
        </tr>
        {/* Row 5 - 仮払金 (enabled only) + empty fill + 控除合計 */}
        <tr className="construction-koujo-section green-row">
          {fifthRowItems.map((item) => (
            <td key={`koujo-key-5-${item.keyId}`} className="construction-border-left construction-border-top construction-border-bottom">
              {item.name}
            </td>
          ))}
          {Array.from({ length: 5 - fifthRowItems.length }, (_, index) => (
            <td key={`koujo-empty-5-${index}`} className="construction-border-left construction-border-top construction-border-bottom">{'\u00A0'}</td>
          ))}
          <td className="construction-border-left construction-border-top construction-border-bottom construction-thick-border-right">
            控除合計
          </td>
        </tr>
        {/* Row 6 - Values for fifth row + value for 控除合計 */}
        <tr className="construction-koujo-section">
          {fifthRowItems.map((item) => (
            <td key={`koujo-value-5-${item.valueId}`} className="construction-border-left construction-thick-border-bottom construction-no-top-border">
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: 5 - fifthRowItems.length }, (_, index) => (
            <td key={`koujo-empty-v5-${index}`} className="construction-border-left construction-thick-border-bottom construction-no-top-border">{'\u00A0'}</td>
          ))}
          <td className="construction-border-left construction-thick-border-bottom construction-no-top-border construction-thick-border-right">
            {getDataValue('totalDeductions', '')}
          </td>
        </tr>
      </>
    );
  };

  // Helper function to render the その他 section: その他支給, その他控除, 年末調整還付金 (from settings, in that order), 差引支給額 in same position (last cell).
  const renderSonotaSection = () => {
    const fillCount = 5 - enabledSonotaItems.length; // 5 cells before 差引支給額 column
    return (
      <>
        {/* Row 1 - Enabled sonota labels from left (その他支給, その他控除, 年末調整還付金), then empty fill, then 差引支給額 */}
        <tr className="construction-sonota-section green-row">
          <td rowSpan={2} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">その他</td>
          {enabledSonotaItems.map((item) => (
            <td
              key={`sonota-key-${item.keyId}`}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >
              {item.name}
            </td>
          ))}
          {Array.from({ length: fillCount }, (_, index) => (
            <td
              key={`sonota-empty-${index}`}
              className="construction-thick-border-top construction-border-left construction-no-bottom-border"
            >{'\u00A0'}</td>
          ))}
          <td className="construction-thick-border-top construction-border-left construction-no-bottom-border construction-thick-border-right">
            差引支給額
          </td>
        </tr>
        {/* Row 2 - Sonota values from left, then empty fill, then 差引支給額 value */}
        <tr className="construction-sonota-section">
          {enabledSonotaItems.map((item) => (
            <td
              key={`sonota-value-${item.valueId}`}
              className="construction-border-left construction-thick-border-bottom construction-border-top"
            >
              {getDataValue(item.keyId, '')}
            </td>
          ))}
          {Array.from({ length: fillCount }, (_, index) => (
            <td
              key={`sonota-empty-value-${index}`}
              className="construction-border-left construction-thick-border-bottom construction-border-top"
            >{'\u00A0'}</td>
          ))}
          <td className="construction-border-left construction-thick-border-bottom construction-border-top construction-thick-border-right">
            {getDataValue('transferAmount', '')}
          </td>
        </tr>
      </>
    );
  };

  // Helper function to render the 労務費明細書 section with month days (12 columns)
  const renderRoumuhiSection = () => {
  const monthDays = generateMonthDays(selectedYear, selectedMonth);
  const lastBandDayCount = monthDays.slice(24, 36).length;
  const teatePaddingCount = Math.max(0, 12 - lastBandDayCount - 1);

  const formatTeateValue = (): string => {
    const num = toNumericValue(constructionData?.allowance);
    if (num === null || num === 0) return '\u00A0';
    return formatNumber(num, true);
  };

  // Helper: format value from DB - always return at least non-breaking space for empty values
  const formatValue = (value : unknown):string => {
    if (value === null || value === undefined || value === '') return '\u00A0'; // Non-breaking space
    if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
      return `出 ${value}`;
    }
    return typeof value === 'string' ? value : JSON.stringify(value);
  };

  return (
    <div className="construction-roumuhi-table-container">
      <table className="construction-table construction-roumuhi-table">
        <tbody>
          {/* Row 1 - Days of the month (12 days in one row) */}
          <tr className="construction-roumuhi-section green-row">
            <td
              rowSpan={6}
              className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right"
            >
              労務費明細書
            </td>
            {monthDays.slice(0, 6).map((day, index) => (
              <td
                key={`roumuhi-day-1-${index}`}
                className="construction-thick-border-top construction-border-left construction-border-bottom"
              >
                {day}
              </td>
            ))}
            {monthDays.slice(6, 12).map((day, index) => (
              <td
                key={`roumuhi-day-1-${index + 6}`}
                className={`construction-thick-border-top construction-border-left construction-border-bottom ${
                  index === 5 ? 'construction-thick-border-right' : ''
                }`}
              >
                {day}
              </td>
            ))}
          </tr>

          {/* Row 2 - Daily values (first 12 days) */}
          <tr className="construction-roumuhi-section">
            {monthDays.slice(0, 6).map((day, index) => (
              <td
                key={`roumuhi-value-2-${index}`}
                className="construction-border-left construction-border-top construction-border-bottom"
              >
                {formatValue(getDailyValue(index + 1))}
              </td>
            ))}
            {monthDays.slice(6, 12).map((day, index) => (
              <td
                key={`roumuhi-value-2-${index + 6}`}
                className={`construction-border-left construction-border-top construction-border-bottom ${
                  index === 5 ? 'construction-thick-border-right' : ''
                }`}
              >
                {formatValue(getDailyValue(index + 7))}
              </td>
            ))}
          </tr>

          {/* Row 3 - Days of the month (continued - next 12 days) */}
          <tr className="construction-roumuhi-section green-row">
            {monthDays.slice(12, 18).map((day, index) => (
              <td
                key={`roumuhi-day-3-${index}`}
                className="construction-border-left construction-border-top construction-border-bottom"
              >
                {day}
              </td>
            ))}
            {monthDays.slice(18, 24).map((day, index) => (
              <td
                key={`roumuhi-day-3-${index + 6}`}
                className={`construction-border-left construction-border-top construction-border-bottom ${
                  index === 5 ? 'construction-thick-border-right' : ''
                }`}
              >
                {day}
              </td>
            ))}
          </tr>

          {/* Row 4 - Daily values (days 13-24) */}
          <tr className="construction-roumuhi-section">
            {monthDays.slice(12, 18).map((day, index) => (
              <td
                key={`roumuhi-value-4-${index}`}
                className="construction-border-left construction-border-top construction-border-bottom"
              >
                {formatValue(getDailyValue(index + 13))}
              </td>
            ))}
            {monthDays.slice(18, 24).map((day, index) => (
              <td
                key={`roumuhi-value-4-${index + 6}`}
                className={`construction-border-left construction-border-top construction-border-bottom ${
                  index === 5 ? 'construction-thick-border-right' : ''
                }`}
              >
                {formatValue(getDailyValue(index + 19))}
              </td>
            ))}
          </tr>

          {/* Row 5 - Days 25-31 labels */}
          <tr className="construction-roumuhi-section green-row">
            {monthDays.slice(24, 30).map((day, index) => (
              <td
                key={`roumuhi-day-5-${index}`}
                className="construction-border-left construction-border-top construction-border-bottom"
              >
                {day}
              </td>
            ))}
            {monthDays.slice(30, 36).map((day, index) => (
              <td
                key={`roumuhi-day-5-${index + 6}`}
                className="construction-border-left construction-border-top construction-border-bottom"
              >
                {day}
              </td>
            ))}
            <td
              key="roumuhi-teate-label"
              className={`construction-border-left construction-border-top construction-border-bottom${
                teatePaddingCount === 0 ? ' construction-thick-border-right' : ''
              }`}
            >
              手当
            </td>
            {Array.from({ length: teatePaddingCount }, (_, index) => (
              <td
                key={`roumuhi-empty-5-${index}`}
                className={`construction-border-left construction-border-top construction-border-bottom${
                  index === teatePaddingCount - 1 ? ' construction-thick-border-right' : ''
                }`}
              >{'\u00A0'}</td>
            ))}
          </tr>

          {/* Row 6 - Daily values (days 25-31) */}
          <tr className="construction-roumuhi-section">
            {monthDays.slice(24, 30).map((day, index) => (
              <td
                key={`roumuhi-value-6-${index}`}
                className="construction-thick-border-bottom construction-border-left construction-border-top"
              >
                {formatValue(getDailyValue(index + 25))}
              </td>
            ))}
            {monthDays.slice(30, 36).map((day, index) => (
              <td
                key={`roumuhi-value-6-${index + 6}`}
                className="construction-thick-border-bottom construction-border-left construction-border-top"
              >
                {formatValue(getDailyValue(index + 31))}
              </td>
            ))}
            <td
              key="roumuhi-teate-value"
              className={`construction-thick-border-bottom construction-border-left construction-border-top${
                teatePaddingCount === 0 ? ' construction-thick-border-right' : ''
              }`}
            >
              {formatTeateValue()}
            </td>
            {Array.from({ length: teatePaddingCount }, (_, index) => (
              <td
                key={`roumuhi-empty-6-${index}`}
                className={`construction-thick-border-bottom construction-border-left construction-border-top${
                  index === teatePaddingCount - 1 ? ' construction-thick-border-right' : ''
                }`}
              >{'\u00A0'}</td>
            ))}
          </tr>

        </tbody>
      </table>
    </div>
  );
};


  const displayNameText = (() => {
    const n = (constructionData?.employeeName || empName || '').trim();
    return n ? `${n}様` : '';
  })();
  const displayNameFontSize = employeeNameDisplayFontSize(displayNameText);

  return (
    <div className="construction-payslip-container">
        <table 
        className="construction-main-table"
          style={{
          fontFamily: payslipSettings.fontFamily,
          fontSize: payslipSettings.fontSize
          }}
        >
                       <thead>
               {/* Header Section */}
               <tr>
                 <td colSpan={2} className="construction-border-rect">組織コード</td>
                 {payslipSettings.showEmployeeCode && <td className="construction-border-rect">社員コード</td>}
                 <td className="construction-border-rect">氏 名</td>
                 <td colSpan={payslipSettings.showEmployeeCode ? 2 : 3} className="construction-no-border construction-title-cell">
                   <span className="construction-title">{title}</span>
                 </td>
               </tr>
               <tr>
                 <td colSpan={2} className="construction-border-rect"></td>
                 {payslipSettings.showEmployeeCode && <td className="construction-border-rect"></td>}
                 <td className="construction-border-rect construction-employee-name-cell">
                   <span
                     className="construction-employee-name-text"
                     style={displayNameFontSize ? { fontSize: displayNameFontSize } : undefined}
                   >
                     {displayNameText}
                   </span>
                 </td>
                 <td colSpan={payslipSettings.showEmployeeCode ? 2 : 3} className="construction-no-border construction-sub-title-cell">
                   <span className="construction-sub-title">
                     {selectedSite}
                     <br />
                     {selectedYear}年{selectedMonth}月分
                   </span>
                 </td>
               </tr>
             </thead>
             <tbody>
               {/* 本社 Row */}
               <tr className="construction-honsha-row">
                 <td colSpan={7} style={{ textAlign: "right" }}></td>
               </tr>
 
               {/* 振込 Section */}
               {enabledFurikomiItems.length > 0 && (
                 <>
                   <tr className="construction-furikomi-section green-row">
                     <td rowSpan={2} className="construction-vertical-text construction-thick-border-left construction-thick-border-top construction-thick-border-bottom construction-thick-border-right">振込</td>
                     {enabledFurikomiItems.map((item, index) => (
                       <td 
                         key={item.keyId}
                         id={item.keyId}
                         className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${index === 5 ? 'construction-thick-border-right' : ''}`}
                       >
                         {item.name}
                       </td>
                     ))}
                     {Array.from({ length: 6 - enabledFurikomiItems.length }, (_, index) => (
                       <td 
                         key={`empty-furikomi-key-${index}`} 
                         className={`construction-thick-border-top construction-border-left construction-no-bottom-border ${(enabledFurikomiItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
                       >{'\u00A0'}</td>
                     ))}
                   </tr>
                  <tr className="construction-furikomi-section">
                    {enabledFurikomiItems.map((item, index) => {
                      const value = getDataValue(item.valueId, '');
                      return (
                        <td 
                          key={item.valueId}
                          id={item.valueId}
                          className={`construction-border-left construction-thick-border-bottom construction-border-top ${index === 5 ? 'construction-thick-border-right' : ''}`}
                        >
                          {value === '' ? '\u00A0' : value}
                        </td>
                      );
                    })}
                    {Array.from({ length: 6 - enabledFurikomiItems.length }, (_, index) => (
                      <td 
                        key={`empty-furikomi-value-${index}`} 
                        className={`construction-border-left construction-thick-border-bottom construction-border-top ${(enabledFurikomiItems.length + index) === 5 ? 'construction-thick-border-right' : ''}`}
                      >&nbsp;</td>
                    ))}
                  </tr>
                 </>
               )}

             {/* Empty separator row */}
             <tr className="construction-separator-row">
               <td colSpan={7} className="construction-white-bg construction-no-border"></td>
             </tr>

                            {/* 勤怠 Section - 2 rows */}
               {renderMultiRowSection(enabledAttendanceItems, '勤怠', 2, 'construction-kintai-section')}

               {/* Empty separator row */}
               <tr className="construction-separator-row">
                 <td colSpan={7} className="construction-white-bg construction-no-border"></td>
               </tr>

               {/* 支給 Section - 4 rows */}
               {renderShikyuSection()}

               {/* Empty separator row */}
               <tr className="construction-separator-row">
                 <td colSpan={7} className="construction-white-bg construction-no-border"></td>
               </tr>

               {/* 控除 Section - 6 rows */}
               {renderKoujoSection()}

               {/* Empty separator row */}
               <tr className="construction-separator-row">
                 <td colSpan={7} className="construction-white-bg construction-no-border"></td>
               </tr>

               {/* その他 Section - 2 rows */}
               {renderSonotaSection()}
             </tbody>
        </table>

        {/* Memo section (その他支給内訳, その他控除内訳, 備考) - between その他 and 労務費明細書 */}
        {payslipSettings.showMemo && (
          <table className="construction-main-table construction-memo-between-table" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '18%' }} />
              <col style={{ width: '82%' }} />
            </colgroup>
            <tbody>
              {enabledNotesItems.length > 0 ? (
                enabledNotesItems.map((item, index) => {
                  const isFirstRow = index === 0;
                  const isLastRow = index === enabledNotesItems.length - 1;
                  return (
                    <tr key={item.id} className="construction-no-name-section construction-memo-section construction-memo-between-section">
                      <td
                        className={`construction-thick-border-left construction-no-right-border construction-no-name-section-key construction-memo-label-cell ${isFirstRow ? 'construction-thick-border-top construction-border-bottom' : 'construction-no-top-border'} ${isLastRow ? 'construction-thick-border-bottom' : 'construction-border-bottom'}`}
                      >
                        {item.name}
                      </td>
                      <td
                        className={`construction-border-left construction-thick-border-right construction-no-name-section-value construction-memo-value-cell ${isFirstRow ? 'construction-thick-border-top construction-border-bottom' : 'construction-no-top-border'} ${isLastRow ? 'construction-thick-border-bottom' : 'construction-border-bottom'}`}
                      >
                        {getDataValue(item.keyId, '')}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr className="construction-no-name-section construction-memo-section construction-memo-between-section">
                  <td colSpan={2} className="construction-thick-border-left construction-thick-border-right construction-thick-border-top construction-thick-border-bottom construction-memo-label-cell">{'\u00A0'}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* 労務費明細書 Section - Separate Table */}
        {renderRoumuhiSection()}

        {/* Company name - Separate Table */}
        {payslipSettings.showCompanyName && (
          <table className="construction-main-table construction-company-memo-table">
            <tbody>
              <tr className="construction-separator-row">
                <td colSpan={7} className="construction-white-bg construction-no-border" style={{ textAlign: "center", fontWeight: "bold", fontSize: `calc(${payslipSettings.fontSize} * 1.6)` }}>株式会社西行土木</td>
              </tr>
            </tbody>
          </table>
        )}
    </div>
  );
}; 